/*
 * Article Grid interactions — scoped per [data-article-grid] root.
 * Client-side filtering by category + batch reveal ("Load more").
 * Hooks: [data-grid-results], [data-pagination-wrapper], [data-filters-container],
 *        [data-filter-link], [data-filter-value], [data-load-more],
 *        [data-article-grid-item], [data-category], [data-hidden-default],
 *        [data-article-card-content], [data-shown-count], [data-total-count],
 *        [data-page-size].
 */
const REDUCED_MOTION = window.matchMedia?.('(prefers-reduced-motion: reduce)');
const FADE_DURATION = 300, DEFAULT_PAGE_SIZE = 6, LG_COLUMNS = 3;

class ArticleGrid {
  constructor(container) {
    this.container = container;
    this.resultsContainer = container.querySelector('[data-grid-results]');
    this.paginationWrapper = container.querySelector('[data-pagination-wrapper]');
    this.filtersContainer = container.querySelector('[data-filters-container]');
    this.pageSize = parseInt(container.dataset.pageSize, 10) || DEFAULT_PAGE_SIZE;
    this.activeCategory = 'all';
    this.revealedCount = this.pageSize;

    this.applyInitialState();
    this.bindEvents();
  }

  bindEvents() {
    this.container.addEventListener('click', (e) => {
      const filterLink = e.target.closest('[data-filter-link]');
      if (filterLink) {
        e.preventDefault();
        this.handleFilter(filterLink.dataset.filterValue || 'all', filterLink);
        return;
      }

      const loadMoreBtn = e.target.closest('[data-load-more]');
      if (loadMoreBtn) {
        e.preventDefault();
        this.handleLoadMore();
      }
    });
  }

  getAllItems() {
    return this.resultsContainer
      ? Array.from(this.resultsContainer.querySelectorAll('[data-article-grid-item]'))
      : [];
  }

  getMatchingItems() {
    const items = this.getAllItems();
    if (this.activeCategory === 'all') return items;
    return items.filter((item) => item.dataset.category === this.activeCategory);
  }

  applyInitialState() {
    // Hide items flagged as beyond the initial page size; everything else stays visible.
    this.getAllItems().forEach((item) => {
      if (item.dataset.hiddenDefault === 'true') {
        item.hidden = true;
      }
    });
    this.updateLoadMoreVisibility();
    this.updateCounter();
    this.updateColumnPadding();
  }

  // The left-padding gutter Liquid bakes in is row-relative to the full,
  // unfiltered block list — correct only while every block is on-screen in
  // that order. Filtering removes blocks from the flow, so the remaining
  // ones reflow into different desktop columns; reapply the gutter against
  // their current visible position instead of their original one.
  updateColumnPadding() {
    this.getAllItems()
      .filter((item) => !item.hidden)
      .forEach((item, index) => {
        item
          .querySelector('[data-article-card-content]')
          ?.classList.toggle('lg:pl-md', index % LG_COLUMNS !== 0);
      });
  }

  applyVisibility() {
    const matching = this.getMatchingItems();
    const matchingSet = new Set(matching);

    this.getAllItems().forEach((item) => {
      if (!matchingSet.has(item)) {
        item.hidden = true;
      }
    });

    matching.forEach((item, index) => {
      item.hidden = index >= this.revealedCount;
    });

    this.updateLoadMoreVisibility();
    this.updateCounter();
    this.updateColumnPadding();
  }

  updateLoadMoreVisibility() {
    const button = this.paginationWrapper?.querySelector('[data-load-more]');
    if (!button) return;
    const matchingTotal = this.getMatchingItems().length;
    button.hidden = this.revealedCount >= matchingTotal;
  }

  updateCounter() {
    if (!this.paginationWrapper) return;
    const matching = this.getMatchingItems();
    const shown = Math.min(this.revealedCount, matching.length);
    this.paginationWrapper.querySelector('[data-shown-count]')?.replaceChildren(String(shown));
    this.paginationWrapper.querySelector('[data-total-count]')?.replaceChildren(String(matching.length));
  }

  async handleFilter(value, clickedLink) {
    if (!this.resultsContainer) return;
    this.filtersContainer?.querySelectorAll('[data-filter-link]').forEach((link) =>
      link.setAttribute('aria-current', link === clickedLink ? 'true' : 'false'),
    );
    this.activeCategory = value;
    this.revealedCount = this.pageSize;
    if (REDUCED_MOTION?.matches) return this.applyVisibility();
    this.resultsContainer.setAttribute('aria-busy', 'true');
    await new Promise((r) => setTimeout(r, FADE_DURATION));
    this.applyVisibility();
    this.resultsContainer.setAttribute('aria-busy', 'false');
  }

  handleLoadMore() {
    const matching = this.getMatchingItems();
    const previousCount = this.revealedCount;
    this.revealedCount = Math.min(this.revealedCount + this.pageSize, matching.length);

    const reduceMotion = REDUCED_MOTION?.matches;
    matching.slice(previousCount, this.revealedCount).forEach((item) => {
      if (reduceMotion) return (item.hidden = false);
      // Reveal at opacity 0, force a layout flush, then drop the entering
      // flag on the next frame so the opacity transition runs.
      item.dataset.entering = 'true';
      item.hidden = false;
      void item.offsetHeight;
      requestAnimationFrame(() => delete item.dataset.entering);
    });

    this.updateLoadMoreVisibility();
    this.updateCounter();
    this.updateColumnPadding();
  }
}

function initSections(container = document) {
  const selector = '[data-article-grid]:not([data-article-grid-init])';
  const roots = container.matches?.(selector) ? [container] : container.querySelectorAll(selector);

  roots.forEach((root) => {
    root.dataset.articleGridInit = 'true';
    new ArticleGrid(root);
  });
}

document.readyState === 'loading'
  ? document.addEventListener('DOMContentLoaded', () => initSections())
  : initSections();
document.addEventListener('shopify:section:load', (e) => initSections(e.target));
